import { TFunction } from 'i18next';
import {
  GUEST_JOURNEY_DEFINITIONS,
  GuestJourneyMetricDefinition,
} from '../guest-journey-definitions';

/**
 * Tab ids match the guest-journey tab key naming used across i18n
 * (`page.guestJourney.tabs.*`) rather than the route paths.
 */
export type ReportFieldsTab =
  | 'activeCustomers'
  | 'sliders'
  | 'churned'
  | 'details'
  | 'stageMovement'
  | 'offerImpact'
  | 'retention'
  | 'benchmarks';

export type ReportFieldItem = {
  name: string;
  desc: string;
};

export type ReportFieldsSection = {
  title: string;
  intro?: string;
  items: ReportFieldItem[];
};

export type ReportFieldsContent = {
  title: string;
  sections: ReportFieldsSection[];
};

/**
 * Glossary item backed by the central metric registry — the name IS the
 * on-page label (same translation key the tab renders), so the drawer can
 * never drift from the UI.
 */
const defItem = (
  t: TFunction,
  definition: GuestJourneyMetricDefinition,
): ReportFieldItem => ({
  name: definition.getLabel(t),
  desc: definition.getDescription(t),
});

/**
 * Shared Movement Indicators reference, appended after the per-tab sections on
 * tabs where movement appears without a per-cell legend (Details and Stage
 * Movement, per the design). Active/Sliding/Churn grids carry their own ⓘ
 * legend drawer instead.
 */
const movementRefSection = (t: TFunction): ReportFieldsSection => ({
  title: t('page.guestJourney.reportFields.movementRef.title'),
  intro: t('page.guestJourney.reportFields.movementRef.intro'),
  items: [
    {
      name: t('page.guestJourney.reportFields.movementRef.movingUp.name'),
      desc: t('page.guestJourney.reportFields.movementRef.movingUp.desc'),
    },
    {
      name: t('page.guestJourney.reportFields.movementRef.movingDown.name'),
      desc: t('page.guestJourney.reportFields.movementRef.movingDown.desc'),
    },
    {
      name: t('page.guestJourney.reportFields.movementRef.stable.name'),
      desc: t('page.guestJourney.reportFields.movementRef.stable.desc'),
    },
  ],
});

const activeCustomersContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.activeCustomers.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.activeCustomers.headline.title'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeNewMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeConversionRate),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeTotalMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeMembersOrderedAllTime),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeTotalSlidersSaved),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.activeTotalChurnedSaved),
      ],
    },
    {
      title: t(
        'page.guestJourney.reportFields.activeCustomers.activityOverview.title',
      ),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.trendingUpOrStable.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.trendingUpOrStable.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.trendingDown.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.trendingDown.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.medianDaysToFirstOrder.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.medianDaysToFirstOrder.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.loyaltyRevenueShare.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.activityOverview.loyaltyRevenueShare.desc',
          ),
        },
      ],
    },
    {
      title: t(
        'page.guestJourney.reportFields.activeCustomers.segmentGrid.title',
      ),
      intro: t('page.guestJourney.reportFields.notes.segTiers'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.spendTiers.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.spendTiers.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.visitTiers.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.visitTiers.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.metricToggle.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.segmentGrid.metricToggle.desc',
          ),
        },
      ],
    },
    {
      title: t('page.guestJourney.reportFields.activeCustomers.charts.title'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountBySpend.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountBySpend.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountByVisits.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountByVisits.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.charts.acquisitionSource.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.charts.acquisitionSource.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountTrends.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.activeCustomers.charts.memberCountTrends.desc',
          ),
        },
      ],
    },
  ],
});

const slidersContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.sliders.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.sliders.headline.title'),
      intro: t('page.guestJourney.reportFields.notes.sliderChurn'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.slidersMembersOrderedAllTime),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.slidersSliders),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.slidersSliderRate),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.slidersMedianDaysFromLastOrder),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.slidersProjectedAnnualRevenue),
      ],
    },
    {
      title: t('page.guestJourney.reportFields.sliders.grid.title'),
      intro: t('page.guestJourney.reportFields.notes.segTiers'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.sliders.grid.stageRetention.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.sliders.grid.stageRetention.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.sliders.grid.cellMovement.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.sliders.grid.cellMovement.desc',
          ),
        },
      ],
    },
    {
      title: t('page.guestJourney.reportFields.sliders.calcNotes.title'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.sliders.calcNotes.sliderWindow.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.sliders.calcNotes.sliderWindow.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.sliders.calcNotes.basketAndVpm.name',
          ),
          desc: t('page.guestJourney.reportFields.notes.vpm'),
        },
      ],
    },
  ],
});

const churnedContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.churned.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.churned.headline.title'),
      intro: t('page.guestJourney.reportFields.notes.sliderChurn'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedChurnThreshold),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedMembersOrderedAllTime),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedChurnedMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedChurnRate),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedMedianDaysFromLastOrder),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.churnedLifetimeRevenue),
      ],
    },
    {
      title: t('page.guestJourney.reportFields.churned.grid.title'),
      intro: t('page.guestJourney.reportFields.notes.segTiers'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.churned.grid.stageRetention.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.churned.grid.stageRetention.desc',
          ),
        },
      ],
    },
    {
      title: t('page.guestJourney.reportFields.churned.calcNotes.title'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.churned.calcNotes.basketAndVpm.name',
          ),
          desc: t('page.guestJourney.reportFields.notes.vpm'),
        },
      ],
    },
  ],
});

const detailsContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.details.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.details.distributionKpis.title'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.details.distributionKpis.visitTiers.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.details.distributionKpis.visitTiers.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.details.distributionKpis.spendTiers.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.details.distributionKpis.spendTiers.desc',
          ),
        },
      ],
    },
    {
      title: t('page.guestJourney.reportFields.details.tables.title'),
      intro: t('page.guestJourney.reportFields.details.tables.intro'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.detailsCurrentTotalMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.detailsTotalVisits),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.detailsTotalSpend),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.detailsAverageOrderSize),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.detailsMedianDaysBetweenVisits),
      ],
    },
    movementRefSection(t),
  ],
});

const stageMovementContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.stageMovement.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.stageMovement.headline.title'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.stageCurrentTotalMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.stageIncomingMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.stageOutgoingMembers),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.stageFrequency),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.stageTotalSpend),
      ],
    },
    {
      title: t('page.guestJourney.reportFields.stageMovement.tables.title'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.movementStage),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.movementCustomersMoved),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.movementVisits),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.movementNetSales),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.movementMovement),
      ],
    },
    movementRefSection(t),
  ],
});

const offerImpactContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.offerImpact.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.offerImpact.headline.title'),
      intro: t('page.guestJourney.reportFields.offerImpact.headline.intro'),
      // Same order as the rendered KPI row (offer-impact.component cardsConfig).
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerAvgCheck30d),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerAvgVisits30d),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerAvgCheck90d),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerAvgVisits90d),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerStageImpact),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerAvgCheckOnRedemption),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerDistributions),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerTotalRedemptions),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerRedemptionRate),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerTotalDiscount),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerTotalSpend),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.offerDiscountRatio),
      ],
    },
    {
      title: t(
        'page.guestJourney.reportFields.offerImpact.outgoingBuckets.title',
      ),
      intro: t(
        'page.guestJourney.reportFields.offerImpact.outgoingBuckets.intro',
      ),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedUp.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedUp.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedOutOfAtRisk.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedOutOfAtRisk.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedIntoNeutral.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.movedIntoNeutral.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.atRisk.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.atRisk.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.didntMove.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.offerImpact.outgoingBuckets.didntMove.desc',
          ),
        },
      ],
    },
  ],
});

const retentionContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.retention.title'),
  sections: [
    {
      title: t('page.guestJourney.reportFields.retention.ltv.title'),
      intro: t('page.guestJourney.reportFields.retention.ltv.intro'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionSpend30),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionSpend90),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionSpend180),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionSpend365),
      ],
    },
    {
      title: t('page.guestJourney.reportFields.retention.visitDetail.title'),
      items: [
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionConversionFromLast),
        defItem(t, GUEST_JOURNEY_DEFINITIONS.retentionMedianDays),
        {
          name: t(
            'page.guestJourney.reportFields.retention.visitDetail.criticalDropOff.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.retention.visitDetail.criticalDropOff.desc',
          ),
        },
      ],
    },
  ],
});

const benchmarksContent = (t: TFunction): ReportFieldsContent => ({
  title: t('page.guestJourney.reportFields.benchmarks.title'),
  sections: [
    {
      title: t(
        'page.guestJourney.reportFields.benchmarks.churnThresholds.title',
      ),
      intro: t(
        'page.guestJourney.reportFields.benchmarks.churnThresholds.intro',
      ),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.benchmarks.churnThresholds.p50.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.benchmarks.churnThresholds.p50.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.benchmarks.churnThresholds.p80.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.benchmarks.churnThresholds.p80.desc',
          ),
        },
      ],
    },
    {
      title: t('page.guestJourney.reportFields.benchmarks.comparison.title'),
      items: [
        {
          name: t(
            'page.guestJourney.reportFields.benchmarks.comparison.yourBrand.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.benchmarks.comparison.yourBrand.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.benchmarks.comparison.cuisineBaseline.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.benchmarks.comparison.cuisineBaseline.desc',
          ),
        },
        {
          name: t(
            'page.guestJourney.reportFields.benchmarks.comparison.variance.name',
          ),
          desc: t(
            'page.guestJourney.reportFields.benchmarks.comparison.variance.desc',
          ),
        },
      ],
    },
  ],
});

export const getReportFieldsContent = (
  t: TFunction,
  tab: ReportFieldsTab,
): ReportFieldsContent => {
  switch (tab) {
    case 'activeCustomers':
      return activeCustomersContent(t);
    case 'sliders':
      return slidersContent(t);
    case 'churned':
      return churnedContent(t);
    case 'details':
      return detailsContent(t);
    case 'stageMovement':
      return stageMovementContent(t);
    case 'offerImpact':
      return offerImpactContent(t);
    case 'retention':
      return retentionContent(t);
    case 'benchmarks':
      return benchmarksContent(t);
  }
};
